import React, { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import AdminLayout from "../../layout/admin";
import { useTranslation } from "react-i18next";
import moment from "moment";

// Component Imports
import GenericDataLayout from "../../components/custom/GenericDataLayout"; 
import GenericTable from "../../components/custom/GenericTable";
import { getMethodApiCall } from "src/services/api";
import { BATCH_YEARS } from "../../utils/constants";
import { ALL_FILTER } from "../../utils/helpers";

const StudentAttendance = () => {
  const { t } = useTranslation();
  const { token } = useAuth();

  // State
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);

  // Dropdown Data
  const [districts, setDistricts] = useState([]);
  const [itis, setItis] = useState([]);

  // Pagination & Filters
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    totalRecords: 0,
    totalPages: 1,
  });

  const [filters, setFilters] = useState({
    district: ALL_FILTER,
    iti_id: ALL_FILTER,
    batch: ALL_FILTER,
    startDate: moment().subtract(1, "month").format("YYYY-MM-DD"),
    endDate: moment().format("YYYY-MM-DD"),
    
  });

  // --- Column Configuration ---
  const columns = [
    { header: "District", accessor: "district" },
    { header: "ITI Name", accessor: "iti_master.iti_name", render: (row) => row.iti_master?.iti_name || '-' },
    { header: "Batch", accessor: "batch_of_trade", render: (row) => row.batch_of_trade || row.trainee?.batch_of_trade || '-' },
    { header: "Student Name", accessor: "trainee.name", render: (row) => row.trainee?.name || row.name || '-' },
    { header: "Check-in Time", accessor: "checkin_time", render: (row) => row.checkin_time ? moment(row.checkin_time).format('h:mm A') : '-' },
    { header: "Checkout Time", accessor: "checkout_time", render: (row) => row.checkout_time ? moment(row.checkout_time).format('h:mm A') : '-' },
    { header: "Hours Clocked", accessor: "hours_logged", render: (row) => {
        return row.hours_logged || '-';
      }
    }
  ];

  // --- API Calls ---

  const fetchDistricts = async () => {
    try {
      const res = await getMethodApiCall(`/v1/api/trainee/get_district_picklist`);
      const resData = res.data;
      if (resData.success) {
        setDistricts(resData.data || []);
        setItis([]); // ITIs will be loaded when a district is selected
      }
    } catch (error) {
      console.error("Error fetching districts:", error);
    }
  };

  const fetchItisForDistrict = async (district) => {
    try {
      if (!district) {
        setItis([]);
        return;
      }
      const res = await getMethodApiCall(`/v1/api/trainee/get_iti_picklist/${encodeURIComponent(district)}`);
      if (res?.status === 200 && res.data?.success) {
        setItis(res.data.data || []);
      }
    } catch (err) {
      console.error('Error fetching itis for district', err);
      setItis([]);
    }
  };

  const fetchData = async () => {
    if (!token) return;

    setLoading(true);
    try {
      const queryParams = new URLSearchParams();

      // Pagination
      queryParams.append("page", pagination.page.toString());
      queryParams.append("limit", pagination.limit.toString());

      // Filters
      if (filters.district && filters.district !== ALL_FILTER) {
        queryParams.append("district", filters.district.trim());
      }
      if (filters.iti_id && filters.iti_id !== ALL_FILTER) {
        queryParams.append("iti_id", filters.iti_id);
      }
      if (filters.batch && filters.batch !== ALL_FILTER) {
        queryParams.append("batch", filters.batch.trim());
      }
      if (filters.startDate) {
        queryParams.append('startDate', filters.startDate);
      }
      if (filters.endDate) {
        queryParams.append('endDate', filters.endDate);
      }
      

      const response = await getMethodApiCall(`/v1/api/admin/attendance?${queryParams}`);

      const responseData = response?.data;

      if (response?.status === 200 && responseData?.data) {
        setData(responseData.data || []);
        setPagination((prev) => ({
          ...prev,
          totalRecords: responseData.pagination?.total || 0,
          totalPages: responseData.pagination?.totalPages || 1,
        }));
      } 
    } catch (error) {
      console.error("Failed to fetch attendance data", error);
    } finally {
      setLoading(false);
    }
  };

  // --- Effects ---

  useEffect(() => {
    if (token) {
      fetchDistricts();
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, pagination.page, pagination.limit, filters]);

  // --- Handlers ---

  const handlePageChange = (newPage) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  const handleLimitChange = (newLimit) => {
    setPagination((prev) => ({ ...prev, limit: newLimit, page: 1 }));
  };

  const handleFilterChange = (key, value) => {
    // If district changes, reset dependent ITI
    if (key === 'district') {
      setFilters((prev) => ({ ...prev, district: value, iti_id: ALL_FILTER }));
      setPagination((prev) => ({ ...prev, page: 1 }));
      // Only fetch ITIs if a specific district is selected (not all)
      if (value && value !== ALL_FILTER) {
        fetchItisForDistrict(value);
      } else {
        setItis([]);
      }
      return;
    }
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleResetFilters = () => {
    setFilters({ district: ALL_FILTER, iti_id: ALL_FILTER, batch: ALL_FILTER, startDate: moment().format("YYYY-MM-DD"), endDate: moment().add(1, "day").format("YYYY-MM-DD") });
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  // --- Layout Configuration ---

  const filterConfig = [
    {
      type: "date",
      label: "Start Date",
      value: filters.startDate,
      onChange: (val) => handleFilterChange('startDate', val),
      max: filters.endDate,
    },
    {
      type: "date",
      label: "End Date",
      value: filters.endDate,
      onChange: (val) => handleFilterChange('endDate', val),
      min: filters.startDate,
    },
    {
      type: "select",
      label: "Batch",
      value: filters.batch,
      options: BATCH_YEARS,
      onChange: (val) => handleFilterChange('batch', val),
    },
    {
      type: "select",
      label: "District",
      value: filters.district,
      options: districts.map((d) => ({ label: d.district, value: d.district })),
      onChange: (val) => handleFilterChange("district", val),
    },
    {
      type: "select",
      label: "ITI Name",
      value: filters.iti_id,
      options: itis.map((i) => ({ label: i.iti_name, value: i.id })),
      onChange: (val) => handleFilterChange("iti_id", val),
      disabled: !filters.district || filters.district === ALL_FILTER,
    },
  
  ];

  return (
    <AdminLayout>
      <GenericDataLayout
        title="Student Attendance"
        subtitle="View and filter student attendance records"
        isLoading={loading}
        filters={filterConfig}
        onResetFilters={handleResetFilters}
        pagination={{
          page: pagination.page,
          limit: pagination.limit,
          totalRecords: pagination.totalRecords,
          onPageChange: handlePageChange,
          onLimitChange: handleLimitChange,
        }}
      >
        <GenericTable 
          columns={columns} 
          data={data}
          pagination={pagination}
          showRowNumber={true}
        />
      </GenericDataLayout>
    </AdminLayout>
  );
};

export default StudentAttendance;